/**
 * PreferencesDialog — Dark mode, push notifications, notification sound.
 * Push-notification state comes from the usePushNotifications hook in the parent.
 */
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Bell, BellOff, Volume2, VolumeX } from "lucide-react";

export default function PreferencesDialog({
  open, onOpenChange,
  notificationsSupported, notificationPermission,
  notificationsEnabled, soundEnabled,
  toggleNotifications, toggleSound,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Preferences</DialogTitle>
          <DialogDescription>Customize your SlideCon experience</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Dark Mode</Label>
              <p className="text-xs text-muted-foreground">Use dark theme</p>
            </div>
            <Switch />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {notificationsEnabled ? (
                <Bell className="w-5 h-5 text-green-500" />
              ) : (
                <BellOff className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <Label>Message Notifications</Label>
                <p className="text-xs text-muted-foreground">
                  {!notificationsSupported
                    ? "Not supported in this browser"
                    : notificationPermission === "denied"
                      ? "Blocked - enable in browser settings"
                      : "Get notified when you receive new DMs"}
                </p>
              </div>
            </div>
            <Switch
              checked={notificationsEnabled}
              onCheckedChange={async () => {
                const result = await toggleNotifications();
                if (result) {
                  toast.success("Notifications enabled!");
                } else if (notificationPermission === "denied") {
                  toast.error("Please enable notifications in your browser settings");
                }
              }}
              disabled={!notificationsSupported || notificationPermission === "denied"}
              data-testid="notification-toggle"
            />
          </div>

          {notificationsEnabled && (
            <div className="flex items-center justify-between pl-8">
              <div className="flex items-center gap-3">
                {soundEnabled ? (
                  <Volume2 className="w-4 h-4 text-blue-500" />
                ) : (
                  <VolumeX className="w-4 h-4 text-slate-400" />
                )}
                <div>
                  <Label className="text-sm">Notification Sound</Label>
                  <p className="text-xs text-muted-foreground">Play sound for new messages</p>
                </div>
              </div>
              <Switch checked={soundEnabled} onCheckedChange={toggleSound} data-testid="sound-toggle" />
            </div>
          )}

          {notificationsSupported && notificationPermission === "default" && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Enable notifications to get alerts when you receive new messages, even when you&apos;re not on this page.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
